import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { Toast, ToastType } from './toast.service';

/**
 * GAP-FE-021B · migrated from decorator @Input/@Output to signal-based input()/output().
 */
@Component({
  selector: 'ui-toast',
  standalone: true,
  imports: [],
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToastComponent {
  readonly toast = input.required<Toast>();
  readonly dismiss = output<string>();

  readonly containerClasses = computed<string>(() => {
    const baseClasses = [
      'flex',
      'items-start',
      'gap-3',
      'p-4',
      'rounded-lg',
      'shadow-lg',
      'border-l-4',
      'bg-bb-bg-secondary',
      'border',
      'border-bb-border',
      'min-w-[300px]',
      'max-w-[400px]',
      'animate-fade-in-down'
    ];

    // GAP-FE-B010 / GAP-FU-02 · five v1.1 variants + legacy `error` alias.
    const borderColors: Record<ToastType, string> = {
      success: 'border-l-bb-accent-green',
      info:    'border-l-bb-accent-blue',
      warning: 'border-l-bb-accent-amber',
      danger:  'border-l-bb-accent-red',
      neutral: 'border-l-bb-text-secondary',
      error:   'border-l-bb-accent-red', // alias for danger (back-compat)
    };

    return [...baseClasses, borderColors[this.toast().type]].join(' ');
  });

  readonly iconClasses = computed<string>(() => {
    const baseClasses = ['w-5', 'h-5', 'flex-shrink-0', 'mt-0.5'];

    const colors: Record<ToastType, string> = {
      success: 'text-bb-accent-green',
      info:    'text-bb-accent-blue',
      warning: 'text-bb-accent-amber',
      danger:  'text-bb-accent-red',
      neutral: 'text-bb-text-secondary',
      error:   'text-bb-accent-red', // alias for danger (back-compat)
    };

    return [...baseClasses, colors[this.toast().type]].join(' ');
  });

  readonly iconPath = computed<string>(() => {
    // Reuses the success/danger/warning/info SVG glyphs; `neutral` reuses the
    // info glyph (chrome-only banner; no dedicated icon needed yet) and
    // `error` aliases the `danger` glyph for back-compat.
    const successPath = 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
    const dangerPath  = 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z';
    const warningPath = 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z';
    const infoPath    = 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';

    const icons: Record<ToastType, string> = {
      success: successPath,
      info:    infoPath,
      warning: warningPath,
      danger:  dangerPath,
      neutral: infoPath,
      error:   dangerPath, // alias for danger (back-compat)
    };

    return icons[this.toast().type];
  });

  /** Handle dismiss */
  onDismiss(): void {
    this.dismiss.emit(this.toast().id);
  }
}
